import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLoading,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { trash } from 'ionicons/icons'
import { useFieldArray, useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { z } from 'zod'

import { client } from '../../../services/client'
import { useToast } from '../../../contexts/toastContext'
import { useBoolean } from '../../../hooks/use-boolean'
import { Card, CardContent } from '../../../components/ui/card'
import { StyledError } from '../../../components/ui/form/errors'
import { PhoneNumberInput, TextInput } from '../../../components/ui/form/input'
import { useUpdateBroadcast } from '../mutations'

type Draft = NonNullable<ReturnType<typeof useUpdateBroadcast>['variables']>
type Props = {
  broadcast: Draft
  disabled?: boolean
}

type FormState = z.infer<typeof schema>

const schema = z.object({
  emailList: z.array(
    z.object({
      email: z
        .literal('')
        .or(z.string().email({ message: 'Please enter a valid email.' })),
    })
  ),
  smsList: z.array(
    z.object({
      e164: z.literal('').or(
        z
          .string()
          .refine(
            (phone) => (phone?.length ? isValidPhoneNumber(phone) : false),
            {
              message: 'Please enter a valid phone number.',
            }
          )
      ),
    })
  ),
})

const defaultValues: FormState = {
  emailList: [{ email: '' }],
  smsList: [{ e164: '' }],
}

async function sendTestBroadcast({
  broadcastId,
  emailList,
  smsList,
}: {
  broadcastId: string
  emailList: FormState['emailList']
  smsList: FormState['smsList']
}) {
  return client.post('functions/sendBroadcastTest', {
    json: {
      objectId: broadcastId,
      emails: emailList.map((email) => email.email).filter(Boolean).length
        ? emailList.map((email) => email.email).filter(Boolean)
        : null,
      e164s: smsList.map((sms) => sms.e164).filter(Boolean).length
        ? smsList.map((sms) => sms.e164).filter(Boolean)
        : null,
    },
  })
}

function useTestBroadcast() {
  const setToast = useToast()

  return useMutation({
    mutationFn: sendTestBroadcast,
    onSuccess: () =>
      setToast({
        message: 'You should receive your test shortly.',
        color: 'yellow',
      }),
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown Error'
      setToast({
        message: `Something went wrong: ${errorMessage}`,
        color: 'danger',
      })
    },
  })
}

function TestBroadcastButton({ broadcast, disabled }: Props) {
  const [isOpen, { on: openModal, off: closeModal }] = useBoolean()

  const { broadcastId } = useParams<{ broadcastId: string }>()

  const updateBroadcast = useUpdateBroadcast()
  const testBroadcast = useTestBroadcast()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormState>({
    defaultValues,
    resolver: zodResolver(schema),
  })

  const emailFields = useFieldArray({ control, name: 'emailList' })
  const smsFields = useFieldArray({ control, name: 'smsList' })

  async function handleSendTest(data: FormState) {
    updateBroadcast.mutate(broadcast, {
      onSuccess: () => {
        testBroadcast.mutate(
          {
            broadcastId,
            ...data,
          },
          {
            onSuccess: closeModal,
          }
        )
      },
    })
  }

  return (
    <>
      <IonButton
        disabled={disabled}
        onClick={openModal}
        className="flex-grow"
        color="yellow"
        size="small"
        fill="solid"
      >
        Test
      </IonButton>

      <IonModal
        isOpen={isOpen}
        onWillDismiss={closeModal}
        onDidDismiss={() => reset()}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton fill="outline" color="secondary" onClick={closeModal}>
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle>Send Test Broadcast</IonTitle>
            <IonButtons slot="end">
              <IonButton
                fill="solid"
                color="yellow"
                onClick={handleSubmit(handleSendTest)}
              >
                Send Test
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="ion-padding">
            <Card>
              <CardContent>
                Note: Sending a Test will first save your Broadcast, including
                any recent changes.
              </CardContent>
            </Card>
          </div>
          <form>
            <div className="ion-padding flex flex-col justify-center gap-6 sm:flex-row">
              {/* EMAIL */}
              <div>
                <p>Emails</p>
                <div className="space-y-4">
                  {emailFields.fields.map((field, index) => (
                    <div key={field.id} className="flex gap-x-2">
                      <div className="w-full">
                        <TextInput
                          control={control}
                          name={`emailList.${index}.email`}
                        />
                        <ErrorMessage
                          as={StyledError}
                          name={`emailList.${index}.email`}
                          errors={errors}
                        />
                      </div>
                      <IonButton
                        onClick={() => emailFields.remove(index)}
                        color="danger"
                      >
                        <IonIcon icon={trash} slot="icon-only" />
                      </IonButton>
                    </div>
                  ))}
                  <IonButton onClick={() => emailFields.append({ email: '' })}>
                    Add Email
                  </IonButton>
                </div>
              </div>

              {/* SMS */}
              <div>
                <p>Phone Numbers</p>
                <div className="space-y-4">
                  {smsFields.fields.map((field, index) => (
                    <div key={field.id} className="flex gap-x-2">
                      <div className="w-full">
                        <PhoneNumberInput
                          control={control}
                          name={`smsList.${index}.e164`}
                        />
                        <ErrorMessage
                          as={StyledError}
                          name={`smsList.${index}.e164`}
                          errors={errors}
                        />
                      </div>

                      <IonButton
                        onClick={() => smsFields.remove(index)}
                        color="danger"
                      >
                        <IonIcon icon={trash} slot="icon-only" />
                      </IonButton>
                    </div>
                  ))}
                  <IonButton onClick={() => smsFields.append({ e164: '' })}>
                    Add Phone Number
                  </IonButton>
                </div>
              </div>
            </div>
          </form>
        </IonContent>
        <IonLoading
          isOpen={updateBroadcast.isLoading || testBroadcast.isLoading}
          message="Preparing your test broadcast..."
        />
      </IonModal>
    </>
  )
}

export { TestBroadcastButton }
